import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import CloseIcon from "@mui/icons-material/Close";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import { useTranslation } from "react-i18next";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import { useBrand } from "../../context/BrandContext";
import { exerciseLabel, isPlaceholderBlock } from "../../utils/planningSubgroups";
import "../PlayerSessionResultsDialog.css";
import "./SyncBlockExerciseDialog.css";

/**
 * Multi-select stations to copy the current training block's exercise onto
 * (same te_wave only). Other blocks stay as planned.
 */
export function SyncBlockExerciseDialog({
  open,
  onClose,
  onConfirm,
  sourceExercise = null,
  lanes = [],
  busy = false,
}) {
  const { t, i18n } = useTranslation();
  const { themeMode } = useBrand();
  const themeClass = themeMode === "light" ? "light-mode" : "dark-mode";
  const lng = i18n.language || "en";
  const [selected, setSelected] = useState(() => new Set());
  const [overwrite, setOverwrite] = useState(true);

  useBodyScrollLock(open);

  const sourceTsgId = sourceExercise?.tsg_id
    ? String(sourceExercise.tsg_id)
    : null;

  const targets = useMemo(
    () =>
      (lanes || []).filter(
        (lane) => lane?.tsg_id && String(lane.tsg_id) !== sourceTsgId
      ),
    [lanes, sourceTsgId]
  );

  useEffect(() => {
    if (!open) {
      setSelected(new Set());
      setOverwrite(true);
      return;
    }
    // Pre-select stations that are empty on this wave (or missing the wave).
    const wave =
      sourceExercise?.te_wave != null && sourceExercise.te_wave !== ""
        ? Number(sourceExercise.te_wave)
        : Number(sourceExercise?.te_order ?? 0);
    const laneTargets = (lanes || []).filter(
      (lane) => lane?.tsg_id && String(lane.tsg_id) !== sourceTsgId
    );
    const pre = new Set();
    for (const lane of laneTargets) {
      const peer = (lane.exercises || []).find((ex) => {
        const w =
          ex?.te_wave != null && ex.te_wave !== ""
            ? Number(ex.te_wave)
            : Number(ex?.te_order ?? 0);
        return w === wave;
      });
      if (!peer || isPlaceholderBlock(peer)) {
        pre.add(String(lane.tsg_id));
      }
    }
    setSelected(
      pre.size > 0
        ? pre
        : new Set(laneTargets.map((l) => String(l.tsg_id)))
    );
    setOverwrite(true);
    // Only re-seed when the dialog opens for a given source block.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- lanes snapshot at open
  }, [open, sourceExercise?.te_id, sourceTsgId]);

  const toggle = (tsgId) => {
    if (busy) return;
    setSelected((prev) => {
      const next = new Set(prev);
      const key = String(tsgId);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAll = () => {
    if (busy) return;
    setSelected(new Set(targets.map((l) => String(l.tsg_id))));
  };

  const clearAll = () => {
    if (busy) return;
    setSelected(new Set());
  };

  const handleConfirm = () => {
    if (busy || selected.size === 0) return;
    onConfirm?.({
      target_tsg_ids: [...selected],
      mode: overwrite ? "overwrite" : "fill_empty",
    });
  };

  if (!open) return null;

  const sourceName =
    exerciseLabel(sourceExercise, lng) ||
    t("trainingBlock") ||
    "Training block";

  return createPortal(
    <div
      className={`player-session-results__backdrop sync-block__backdrop ${themeClass}`}
      onClick={busy ? undefined : onClose}
      role="presentation"
    >
      <div
        className={`player-session-results__sheet sync-block__sheet ${themeClass}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t("syncBlockExercise") || "Sync training block"}
      >
        <div className="player-session-results__handle" />
        <button
          type="button"
          className="player-session-results__close"
          onClick={onClose}
          disabled={busy}
          aria-label={t("close") || "Close"}
        >
          <CloseIcon />
        </button>

        <div className="player-session-results__content">
          <div className="player-session-results__header">
            <h2 className="player-session-results__title">
              <SyncAltIcon sx={{ fontSize: 22, verticalAlign: -4, mr: 0.75 }} />
              {t("syncBlockExercise") || "Sync this block"}
            </h2>
            <p className="player-session-results__subtitle sync-block__subtitle">
              {t("syncBlockExerciseHint") ||
                "Apply this training block’s exercise to other stations. Other blocks stay as planned."}
            </p>
            <p className="sync-block__source">
              <span className="sync-block__source-label">
                {t("currentExercise") || "Current"}
              </span>
              <span className="sync-block__source-name">{sourceName}</span>
            </p>
          </div>

          <div className="sync-block__toolbar">
            <button
              type="button"
              className="sync-block__link"
              onClick={selectAll}
              disabled={busy || targets.length === 0}
            >
              {t("syncBlockSelectAll") || "Select all"}
            </button>
            <button
              type="button"
              className="sync-block__link"
              onClick={clearAll}
              disabled={busy || selected.size === 0}
            >
              {t("syncBlockClear") || "Clear"}
            </button>
          </div>

          <div className="player-session-results__body sync-block__body">
            {targets.length === 0 ? (
              <p className="sync-block__empty">
                {t("syncBlockNoStations") ||
                  "Split the group into stations first to sync a block."}
              </p>
            ) : (
              <ul className="sync-block__list">
                {targets.map((lane) => {
                  const id = String(lane.tsg_id);
                  const checked = selected.has(id);
                  const label = lane.is_main
                    ? t("mainGroup") || lane.name || "Main group"
                    : lane.name || t("subGroup") || "Subgroup";
                  return (
                    <li key={id}>
                      <label
                        className={`sync-block__row${
                          checked ? " sync-block__row--on" : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={busy}
                          onChange={() => toggle(id)}
                        />
                        <span
                          className="sync-block__swatch"
                          style={{ background: lane.color || "#0877BC" }}
                          aria-hidden
                        />
                        <span className="sync-block__row-label">{label}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}

            <label className="sync-block__mode">
              <input
                type="checkbox"
                checked={overwrite}
                disabled={busy}
                onChange={(e) => setOverwrite(e.target.checked)}
              />
              <span>
                {t("syncBlockOverwrite") ||
                  "Replace exercises already planned on those stations for this block"}
              </span>
            </label>
          </div>

          <footer className="sync-block__footer">
            <button
              type="button"
              className="sync-block__btn sync-block__btn--ghost"
              onClick={onClose}
              disabled={busy}
            >
              {t("cancel") || "Cancel"}
            </button>
            <button
              type="button"
              className="sync-block__btn sync-block__btn--primary"
              onClick={handleConfirm}
              disabled={busy || selected.size === 0}
            >
              {busy
                ? t("syncBlockExerciseBusy") || "Syncing…"
                : t("syncBlockExerciseApply") || "Sync block"}
            </button>
          </footer>
        </div>
      </div>
    </div>,
    document.body
  );
}
